import { useCallback, useEffect, useReducer } from 'react';

import { TwoFactorAuthentication } from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';

import handleQueries from '../business/LoginPopup.handleQueries';
import reducer, { ActionType, getInitialState, State } from '../business/LoginPopup.reducer';

/**
 * All possible callbacks and actions for the LoginPopup hook.
 */
interface Handlers {
  /**
   * Switch to multiple authentication mode.
   */
  handleSwitchToMultipleMode: () => void;
  /**
   * Update the two-factor authentication code.
   *
   * ## Example:
   * ```ts
   * const handleCodeChange = useCallback(
   *   (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
   *     handleCodeUpdate(e.target.value);
   *   }, [handleCodeUpdate],
   * );
   * ```
   */
  handleCodeUpdate: (code: string) => void;
  /**
   * Submit the two-factor authentication code for verification.
   */
  handleCodeSubmit: () => void;
  /**
   * Sets the "Remember Device" checkbox.
   */
  handleRememberDeviceChange: (rememberDevice: boolean) => void;
  /**
   * Sets the selected two-factor authentication.
   */
  handleTwoFactorAuthenticationChange: (twoFactorAuthentication: TwoFactorAuthentication) => void;
  /**
   * Resend the code to the user, if using SMS.
   */
  handleResendCode: () => void;
  /**
   * Call user instead of sending SMS code.
   */
  handleCallMe: () => void;
  /**
   * Closes the dialog.
   */
  handleClose: () => void;
  /**
   * Clears code and error.
   */
  handleClearInput: () => void;
}

interface Props {
  isOpen: boolean;
  /**
   * Requests the view to close.
   */
  onClose: () => void;
  /**
   * Requests to proceed with the login successfully.
   */
  onSuccessfulTwoFactorChallenge: () => void;
}

/**
 * React hook to encapsulate Login Popup business logic.
 * @param callbacks To enable the hook to work with with external modules.
 * {@link Callbacks More information here}.
 * @returns The {@link State state} for the component and all possible {@link Handlers actions}.
 */
function useLoginPopup(props: Props): [State, Handlers] {
  const { isOpen, onClose, onSuccessfulTwoFactorChallenge } = props;
  const [state, dispatch] = useReducer(reducer, getInitialState({ isOpen }));

  const handleSwitchToMultipleMode = useCallback(
    () => dispatch({ type: ActionType.SetIsDefaultTwoFactorAuthenticationMode, payload: false }),
    []
  );

  const handleCodeUpdate = useCallback(
    (code: string) => dispatch({ type: ActionType.UpdateCode, payload: code }),
    []
  );

  const handleCodeSubmit = useCallback(() => dispatch({ type: ActionType.SubmitCode }), []);

  const handleRememberDeviceChange = useCallback(
    (rememberDevice: boolean) =>
      dispatch({ type: ActionType.SetIsRememberDevice, payload: rememberDevice }),
    []
  );

  const handleTwoFactorAuthenticationChange = useCallback(
    (twoFactorAuthentication: TwoFactorAuthentication) =>
      dispatch({
        type: ActionType.SelectTwoFactorAuthentication,
        payload: twoFactorAuthentication,
      }),
    []
  );

  const handleResendCode = useCallback(() => dispatch({ type: ActionType.ResendCode }), []);

  const handleCallMe = useCallback(
    () => dispatch({ type: ActionType.BeginCallAuthentication }),
    []
  );

  const handleClose = useCallback(() => {
    dispatch({ type: ActionType.SetIsOpen, payload: false });
    onClose();
  }, [onClose]);

  const handleClearInput = useCallback(
    () => dispatch({ type: ActionType.UpdateCode, payload: '' }),
    []
  );

  useEffect(
    () => {
      handleQueries(state.queries, dispatch, { onClose, onSuccessfulTwoFactorChallenge });
      /**
       * Queries need to be cleared so that it's safe to call handleQueries multiple times.
       * @todo (Mark) investigate this.
       */
    },
    // Intentionally only run this effect when state.queries changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.queries]
  );

  // Knockout (or React) will eventually set isOpen to true which will initiate open dialog logic
  useEffect(() => {
    dispatch({ type: ActionType.SetIsOpen, payload: isOpen });
  }, [isOpen]);

  return [
    state,
    {
      handleSwitchToMultipleMode,
      handleCodeUpdate,
      handleCodeSubmit,
      handleRememberDeviceChange,
      handleTwoFactorAuthenticationChange,
      handleResendCode,
      handleClose,
      handleClearInput,
      handleCallMe,
    },
  ];
}

export default useLoginPopup;
export type { Handlers, Props, State };
